import React, { useState, useEffect } from 'react';
import { FiSearch, FiPackage, FiMapPin, FiCalendar, FiTruck, FiCheckCircle, FiImage, FiDownload } from 'react-icons/fi';
import { trackDelivery } from '../../lib/supabase/tracking';
import { useParams } from 'react-router-dom';

const DeliveryTracker = () => {
    const { trackingId: urlTrackingId } = useParams();
    const [trackingId, setTrackingId] = useState(urlTrackingId || '');
    const [tracking, setTracking] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selectedImage, setSelectedImage] = useState(null);

    useEffect(() => {
        if (urlTrackingId) {
            handleTrack({ preventDefault: () => { } });
        }
    }, [urlTrackingId]);

    const handleTrack = async (e) => {
        e.preventDefault();

        if (!trackingId.trim()) {
            setError('Please enter a tracking ID');
            return;
        }

        setLoading(true);
        setError(null);
        setTracking(null);

        const { data, error: trackError } = await trackDelivery(trackingId);

        if (trackError || !data) {
            setError('Tracking ID not found. Please check and try again.');
            setLoading(false);
            return;
        }

        setTracking(data);
        setLoading(false);
    };

    const getStatusInfo = (status) => {
        const statusMap = {
            'in_delivery': { label: 'Out for Delivery', color: 'bg-indigo-600', icon: FiTruck },
            'completed': { label: 'Delivered', color: 'bg-green-600', icon: FiCheckCircle },
            'cancelled': { label: 'Cancelled', color: 'bg-gray-500', icon: FiPackage },
        };
        return statusMap[status] || { label: status, color: 'bg-gray-400', icon: FiPackage };
    };

    const formatDate = (date) => {
        if (!date) return 'Not scheduled';
        return new Date(date).toLocaleDateString('en-AU', {
            weekday: 'short',
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatDateTime = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleString('en-AU', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="min-h-screen">
            <div className="max-w-4xl mx-auto pt-24">
                {/* Header */}
                <div className="text-center mb-8">
                    <a
                        href='https://notifyapp.com.au'
                        target='_blank'
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center justify-center mb-4"
                    >
                        <img className='w-80' src={`${process.env.PUBLIC_URL}/assets/notify-cropped-wide.png`} alt="" />
                    </a>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        Track Your Delivery
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Enter your tracking ID to see the current status of your order
                    </p>
                </div>

                {/* Search Form */}
                <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-xl p-6 mb-8">
                    <form onSubmit={handleTrack} className="flex gap-3">
                        <div className="flex-1 relative">
                            <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                value={trackingId}
                                onChange={(e) => setTrackingId(e.target.value.toUpperCase())}
                                placeholder="Enter tracking ID (e.g., ABC12345)"
                                className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 dark:border-zinc-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-zinc-900 dark:text-white text-lg"
                                maxLength={8}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                        >
                            {loading ? 'Tracking...' : 'Track'}
                        </button>
                    </form>

                    {error && (
                        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                            <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
                        </div>
                    )}
                </div>

                {/* Tracking Results */}
                {tracking && (
                    <div className="space-y-6">
                        {/* Status Card */}
                        <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-xl p-8">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Tracking ID</p>
                                    <p className="text-2xl font-bold text-gray-900 dark:text-white font-mono">
                                        {tracking.short_id}
                                    </p>
                                </div>
                                <div className={`px-4 py-2 ${getStatusInfo(tracking.status).color} rounded-full`}>
                                    <div className="flex items-center gap-2 text-white">
                                        {React.createElement(getStatusInfo(tracking.status).icon, { className: "w-5 h-5" })}
                                        <span className="font-semibold">{getStatusInfo(tracking.status).label}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Delivery Info Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {tracking.delivery_date && (
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <FiCalendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Scheduled Delivery</p>
                                            <p className="font-semibold text-gray-900 dark:text-white">
                                                {formatDate(tracking.delivery_date)}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {tracking.delivery_address && (
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <FiMapPin className="w-5 h-5 text-green-600 dark:text-green-400" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Delivery Address</p>
                                            <p className="font-semibold text-gray-900 dark:text-white">
                                                {tracking.delivery_address}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {tracking.assigned_driver_name && (
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <FiTruck className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Driver</p>
                                            <p className="font-semibold text-gray-900 dark:text-white">
                                                {tracking.assigned_driver_name}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {tracking.company_name && (
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <FiPackage className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Company</p>
                                            <p className="font-semibold text-gray-900 dark:text-white">
                                                {tracking.company_name}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Customer Info */}
                        {tracking.customer_name && (
                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Customer</p>
                                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {tracking.customer_name}
                                </p>
                            </div>
                        )}

                        {/* Delivery Photos */}
                        {tracking.delivery_photos && tracking.delivery_photos.length > 0 && (
                            <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-xl p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <FiImage className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                        Delivery Photos
                                    </h3>
                                    <span className="bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-sm font-medium px-3 py-1 rounded-full">
                                        {tracking.delivery_photos.length} photo{tracking.delivery_photos.length !== 1 ? 's' : ''}
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    {tracking.delivery_photos.map((photo) => (
                                        <div
                                            key={photo.id}
                                            className="group relative aspect-square rounded-xl overflow-hidden bg-gray-100 dark:bg-zinc-900 cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
                                            onClick={() => setSelectedImage(photo)}
                                        >
                                            <img
                                                src={photo.url}
                                                alt={photo.filename}
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                                <FiSearch className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                                                <p className="text-white text-xs truncate">{photo.uploaded_by_name}</p>
                                                <p className="text-white/70 text-xs">{formatDateTime(photo.uploaded_at)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>


            {/* Image Modal */}
            {selectedImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
                    onClick={() => setSelectedImage(null)}
                >
                    <div className="relative max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setSelectedImage(null)}
                            className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
                        >
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <img
                            src={selectedImage.url}
                            alt={selectedImage.filename}
                            className="w-full h-auto rounded-lg"
                        />
                        <div className="mt-4 flex items-center justify-between text-white">
                            <div>
                                <p className="font-medium">{selectedImage.uploaded_by_name}</p>
                                <p className="text-sm text-gray-300">{formatDateTime(selectedImage.uploaded_at)}</p>
                            </div>
                            <a
                                href={selectedImage.url}
                                download={selectedImage.filename}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                            >
                                <FiDownload className="w-4 h-4" />
                                Download
                            </a>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default DeliveryTracker;